<template>
  <transition name="fade" mode="out-in">
    <ErrorBoundary v-if="examRoute && !isLoading">
      <component
        v-if="examComponent"
        :is="examComponent"
        :exam="examStoreName"
        :isPractice="isPractice"
        :isExamComplete="isExamComplete"
        :trial="currentTrial"
        :displayType="displayType"
        :step="step"
        :phase="phase"
        ref="exam"
        @next="handleNext"
        @prev="handlePrev"
      >
        <Stage>
          <template #left v-if="hasNav">
            <NavButton
              :disabled="prevDisabled"
              @click="handlePrev"
              direction="previous"
            />
          </template>
          <component
            v-if="component"
            :is="component"
            :handleTrialResponse="handleTrialResponse"
            :trial="currentTrial"
            :imageCache="imageCache"
            :step="step"
            :isPractice="isPractice"
            @next="handleNext"
            @prev="handlePrev"
          />
          <div v-else>
            no trial <span>"{{ this.currentTrial.component }}"</span> exists
          </div>
          <template #right v-if="hasNav">
            <NavButton @click="handleNext" :disabled="nextDisabled" />
          </template>
        </Stage>
      </component>
      <div v-else>no exam component '{{ this.examComponent }}' found</div>
    </ErrorBoundary>
    <Stage v-else>
      <div class="loader-container">
        <Progress indeterminate />
      </div>
      <Modal
        :value="showErrorDialog"
        :title="$t('error_generic_header')"
        :buttonLabel="$t('button_close')"
      >
        {{ $t('error_generic_exam_failed_body') }}
        <template #actions>
          <Button
            :label="$t('button_close')"
            @click="redirectHome"
          />
        </template>
      </Modal>
    </Stage>
  </transition>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState
} from 'vuex'

import {
  Button, Modal, Progress, Stage
} from '@neurotrack/web-shared-components'
import ErrorBoundary from '@/components/ErrorBoundary'
import NavButton from '@/components/NavButton'
import { Analytics } from '@/util/env'
import { getExamStoreName, getExamType } from '@/util/examUtils'
import VideoRecorder from '@/util/VideoRecorder'
import { components } from './ess/index'

export default {
  name: 'ESSContainer',
  components: {
    Button, ErrorBoundary, Modal, NavButton, Progress, Stage
  },
  mounted() {
    this.initialize()
  },
  props: {
    examRoute: {
      type: String,
      default: '',
      required: true
    }
  },
  data() {
    return {
      eopRecorder: undefined,
      eopTimer: undefined,
      showErrorDialog: false
    }
  },
  beforeDestroy() {
    clearInterval(this.eopTimer)
    if (this.stream) {
      this.stopCamera()
    }
  },
  methods: {
    ...mapMutations(['showModal', 'hideModal']),
    ...mapActions('video', ['stopCamera', 'uploadEoP']),
    ...mapGetters({
      getAssessmentId: 'exam/getAssessmentIdForExam'
    }),
    initialize() {
      const { examStoreName: examStore, examRoute } = this

      // In case perception_start_pending has been left in a true state
      this.$store.commit(`exam/${this.examStoreName}/SET_PERCEPTION_START_PENDING`, false)

      // Legacy specific fix to resolve perception start not firing between different user-logins
      this.$store.commit(`exam/${this.examStoreName}/RESET_PERCEPTION_DATA`)

      // this is because you may be returning, and restoring
      // the user state instead of explicit login ~JB (🤔)
      if (this.$store.state.activities.activities) {
        this.fetchExam(examStore, examRoute)
      } else {
        this.$store.subscribe(({ type }) => {
          if (type === 'activities/SET_ACTIVITIES') {
            this.fetchExam(examStore, examRoute)
          }
        })
      }
      this.$store.commit(`exam/${examStore}/RESET_EXAM`, null, { root: true })

      // Start EoP if we have camera access
      if (this.accessToCamera) {
        this.eopRecorder = new VideoRecorder(this.stream, this.eopHandler)
        // we are sending 5s clips for duration of the exam
        this.eopTimer = setInterval(() => {
          if (this.eopRecorder.isRecording) {
            this.eopRecorder.stop()
          }

          this.eopRecorder.record('eop')
        }, 5000)
      }
    },
    eopHandler(videos) {
      const { clip, contentType } = videos.pop()
      const assessmentId = this.getAssessmentId?.()(this.examStoreName)
      this.uploadEoP({
        clip,
        userId: this.profile.user_id,
        assessmentId,
        contentType
      })
    },
    fetchExam(examStore, examRoute) {
      this.$store.dispatch(`exam/${examStore}/fetchExam`, examRoute)
        .catch(error => {
          this.$store.dispatch(`exam/${examStore}/finishPerception`, 'QUIT_EARLY')
            .finally(() => {
              this.showErrorDialog = true
            })
          console.warn('Error fetching exam', error)
        })
    },
    handlePrev() {
      this.$store.dispatch(`exam/${this.examStoreName}/prev`, undefined, {
        root: true
      })
    },
    handleNext() {
      if (this.isExamComplete) {
        this.$router.push('/')
        return
      }
      this.$store.dispatch(`exam/${this.examStoreName}/next`, undefined, {
        root: true
      })
    },
    handleTrialResponse(e) {
      this.$refs.exam.handleResponse?.({
        event: e,
        trial: this.currentTrial
      })
    },
    redirectHome() {
      this.$router.push({ name: 'Home' })
    }
  },
  watch: {
    currentPhase() {
      Analytics.track('Assessment', {
        exam: this.exam,
        phase: this.currentPhase.phase_display_type
      })
    },
    isExamComplete(complete) {
      if (complete) {
        Analytics.track('Assessment', {
          exam: this.exam,
          complete: true
        })

        clearInterval(this.eopTimer)
        if (this.eopRecorder?.isRecording) {
          this.eopRecorder.stop()
          this.stopCamera()
        }
      }
    }
  },
  computed: {
    ...mapState('video', ['accessToCamera', 'stream']),
    ...mapState('user', ['profile']),
    examStoreName() {
      return getExamStoreName(getExamType(this.examRoute))
    },
    /**
     * NOTE: mapState will not work here with a dynamic namespace
     *
     * @see https://stackoverflow.com/questions/55927452/vuex-dynamic-namespaces-in-binding-helpers-mapstate
     * @returns {object}
     */
    state() {
      return this.$store.state.exam[this.examStoreName]
    },
    imageCache() {
      return this.state.imageCache
    },
    phase() {
      return this.state.phase || 0
    },
    step() {
      return this.state.trial || 0
    },
    phases() {
      return this.state.phases
    },
    routes() {
      return this.state.routes
    },
    /**
     * e.g. EXAM_IMPRINT_MEMORY_ASSESSMENT
     * 
     * @returns {string}
     */
    type() {
      return this.state.type || ''
    },
    isLoading() {
      return this.state.isLoading || !this.state.images_preloaded
    },
    currentPhase() {
      return this.phases[this.phase]
    },
    currentTrial() {
      return this.phases[this.phase].trials[this.step]
    },
    /**
     * e.g. exam_imprint_memory_assessment
     * 
     * @returns {string}
     */
    examComponent() {
      return this.type.toLowerCase()
    },
    navigation() {
      return this.currentTrial.component_data.navigation
    },
    prevDisabled() {
      return !this.navigation?.includes('previous')
    },
    nextDisabled() {
      return !this.navigation?.includes('next')
    },
    hasNav() {
      const hasNav =
        this.navigation?.includes('next')
        || this.navigation?.includes('previous')
      return hasNav
    },
    component() {
      const componentName = this.currentTrial.component
      const component = components[componentName]
      if (!component) {
        console.error(
          `no trial component ${componentName} found in ess view directory, or registered in index.js`
        )
      }
      return component
    },
    isPractice() {
      return this.phases[this.phase].phase_configuration.tutorial_mode
    },
    isExamComplete() {
      return (
        this.phase === this.routes.length - 1
        && this.routes[this.phase] === this.step + 1
      )
    },
    displayType() {
      return this.phases[this.phase].phase_display_type
    }
  }
}
</script>

<style scoped>
.loader-container {
  padding: 2rem;
  width: 30rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
